import { baseApi } from './baseApi';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  avatarIcon?: string | null;
  /** Restaurantes a los que pertenece (multi-restaurante). */
  restaurantIds?: string[];
  /** Con cuál abre sesión por defecto. */
  primaryRestaurantId?: string | null;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  avatarIcon?: string | null;
  /** Omitirlos = el restaurante actual, el comportamiento de siempre. */
  restaurantIds?: string[];
  primaryRestaurantId?: string;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  /**
   * OJO: este PUT reemplaza al usuario completo, así que omitirlo BORRA el icono
   * que la persona tuviera. Quien edite un usuario debe mandar siempre el valor
   * actual (el modal de Usuarios lo precarga).
   */
  avatarIcon?: string | null;
  /** A diferencia del avatar, omitirlos NO toca las membresías. */
  restaurantIds?: string[];
  primaryRestaurantId?: string;
}

export interface ResetPasswordRequest {
  newPassword: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface UserLimitInfo {
  /** Asientos ocupados en la sucursal actual. */
  currentUsers: number;
  /** Límite efectivo por sucursal (con los asientos de la prueba si aplica). */
  maxUsers: number;
  /**
   * Lo guardado en la organización, sin asientos de prueba. Al contratar un plan el
   * límite queda en el mayor entre lo que da ese plan y esto.
   */
  grantedMaxUsers?: number;
  plan: string;
  canCreateUsers: boolean;
  isTrialExpired: boolean;
  trialEndDate?: string;
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => '/users',
      providesTags: ['User'],
    }),
    getWaiters: builder.query<User[], void>({
      query: () => '/users/waiters',
      providesTags: ['User'],
    }),
    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (newUser) => ({
        url: '/users',
        method: 'POST',
        body: newUser,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<User, { id: string } & UpdateUserRequest>({
      query: ({ id, ...patch }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        'User' // Invalidate list cache to refresh all queries
      ],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    resetPassword: builder.mutation<void, { id: string; newPassword: string }>({
      query: ({ id, newPassword }) => ({
        url: `/users/${id}/reset-password`,
        method: 'POST',
        body: { newPassword },
      }),
    }),
    /**
     * Cambia el avatar de quien está logueado. Endpoint propio porque el PUT de
     * usuario exige rol de gestión: un mesero puede cambiar su avatar, no su rol.
     * El id sale del token, por eso no se manda.
     */
    updateMyAvatar: builder.mutation<{ avatarIcon: string | null }, string | null>({
      query: (avatarIcon) => ({
        url: '/users/me/avatar',
        method: 'PUT',
        body: { avatarIcon },
      }),
      invalidatesTags: ['User'],
    }),
    getRoles: builder.query<Role[], void>({
      query: () => '/users/roles',
      providesTags: ['Role'],
    }),
    /**
     * Plan, vigencia de la prueba y asientos. Va a /organization/seat-limit, abierto a
     * cualquier rol: /users/limit-info exige rol de gestión, y con la prueba de 3
     * usuarios un mesero recibía 403, caía a /select-plan en cada login y nunca veía
     * el aviso de fin de prueba.
     */
    getUserLimitInfo: builder.query<UserLimitInfo, void>({
      query: () => '/organization/seat-limit',
      providesTags: ['User'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetWaitersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
  useUpdateMyAvatarMutation,
  useGetRolesQuery,
  useGetUserLimitInfoQuery,
} = usersApi;
